'use client';

/**
 * The spatial experience layer — a full-viewport, non-interactive canvas
 * layer mounted behind the DOM (z-index 0; all site content sits above it).
 *
 * Interaction model (deliberate): the world is an ambient visualization, not
 * a click surface. The layer is pointer-events:none so the DOM above it stays
 * fully operable (text selection, links, focus). Every entity the world shows
 * — projects, research, agents, knowledge nodes — is reachable through its own
 * URL and through Ask Lab's action protocol, so no information or navigation
 * depends on picking a 3D object. This keeps the content-first, progressive-
 * enhancement contract intact; the zone spaces' pointer handlers are retained
 * for the day picking is enabled but are inert under the current DOM stack.
 *
 * Boot order:
 *  1. Server render / first paint: only the CSS backdrop — zero JS cost.
 *  2. On mount, quality is negotiated (src/world/quality.ts). Missing WebGL
 *     → 'static'. Reduced motion keeps the canvas mounted but makes the
 *     world settle in place.
 *  3. Otherwise the Scene chunk (three.js) loads lazily via next/dynamic
 *     and fades in over ~1.5 s once its first frame has rendered.
 */

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useWorld } from '../lab/store';
import { detectQuality } from './quality';
import LabAudio from './LabAudio';
import styles from './ExperienceLayer.module.css';

const Scene = dynamic(() => import('./Scene'), { ssr: false });

export default function ExperienceLayer() {
  const mode = useWorld((s) => s.mode);
  const pathname = usePathname();
  const [detected, setDetected] = useState(false);
  const [sceneLive, setSceneLive] = useState(false);

  // On the home route the world is the star; on every content route it
  // recedes behind the copy so text never competes with 3D labels.
  const recede = pathname !== '/' && !/^\/(?:en|ru)\/?$/.test(pathname ?? '');

  useEffect(() => {
    const decision = detectQuality();
    const store = useWorld.getState();
    store.setReducedMotion(decision.reducedMotion);
    if (decision.webglFailed) store.setWebglFailed(true);
    store.setMode(decision.mode);
    setDetected(true);

    // Reduced motion changes the world animation policy, not whether the
    // laboratory exists. This preserves the spatial scene without motion.
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      useWorld.getState().setReducedMotion(mq.matches);
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const handleReady = useCallback(() => setSceneLive(true), []);

  const renderable = detected && mode !== 'static';

  // StudyNinja owns the whole viewport and renders its own scene-specific
  // spatial systems. Keeping the generic laboratory world mounted behind it
  // would spend GPU budget without contributing a visible pixel.
  if (/\/(?:en|ru)\/projects\/studyninja\/?$/.test(pathname)) return null;

  return (
    <div className={styles.layer} aria-hidden="true">
      <div className={styles.backdrop} />
      {renderable ? (
        <div
          className={[
            styles.world,
            sceneLive ? styles.worldLive : '',
            recede ? styles.worldRecede : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <Scene onReady={handleReady} />
        </div>
      ) : null}
      <LabAudio />
    </div>
  );
}
